/**
 * Main App Component
 * Routing and layout for the application
 */
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import HistoryPage from './pages/HistoryPage';
import ProfileCompletionPage from './pages/ProfileCompletionPage';
import TemplatesPage from './pages/TemplatesPage';
import AdminUsersPage from './pages/AdminUsersPage';
import MasEditorPage from './at-editor/MasEditorPage';
import LaunchCountdown from './components/LaunchCountdown';

// Create Material-UI theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#9c27b0',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 500 },
  },
  shape: { borderRadius: 8 },
});

// Until LAUNCH_AT everyone but admins sees the launch countdown; when it runs
// out the app opens by itself (no reload). Admins sign in through the tiny
// "admin" link on the countdown. Set MAINTENANCE_MODE to false to switch the
// countdown off entirely.
const MAINTENANCE_MODE = true;
// Launch: Friday 6 November 2026, 12:00 AM Sri Lanka time (midnight after Thursday 5th)
const LAUNCH_AT = new Date('2026-11-06T00:00:00+05:30').getTime();
const LAUNCH_LABEL = 'Friday, 6 November · 12:00 AM (Sri Lanka)';

const LoadingScreen = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
    <CircularProgress size={60} />
  </Box>
);

// Maintenance: admins use the app as normal; everyone else sees the screen.
// Signed-out visitors reach the login and create-account pages only after
// opening the admin link (remembered for the tab, so moving between the two
// pages, e.g. an admin creating their password, keeps working).
const ADMIN_SIGNIN_KEY = 'admin_signin';
const MaintenanceGate = ({ children }) => {
  const { loading, user, logout } = useAuth();
  const location = useLocation();
  // Re-check every second so the app opens the moment the countdown ends
  const [launched, setLaunched] = useState(() => Date.now() >= LAUNCH_AT);
  useEffect(() => {
    if (launched) return undefined;
    const id = setInterval(() => { if (Date.now() >= LAUNCH_AT) setLaunched(true); }, 1000);
    return () => clearInterval(id);
  }, [launched]);
  if (!MAINTENANCE_MODE || launched) return children;
  if (location.pathname === '/login' && new URLSearchParams(location.search).has('admin')) {
    try { sessionStorage.setItem(ADMIN_SIGNIN_KEY, '1'); } catch (_) {}
  }
  if (loading) return <LoadingScreen />;
  if (user?.role === 'admin') return children;
  let adminSignIn = false;
  try { adminSignIn = sessionStorage.getItem(ADMIN_SIGNIN_KEY) === '1'; } catch (_) {}
  if (!user && adminSignIn && ['/login', '/signup'].includes(location.pathname)) return children;
  return <LaunchCountdown launchAt={LAUNCH_AT} launchLabel={LAUNCH_LABEL} user={user} onLogout={logout} />;
};

// Requires authentication + full_name set
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user && !user.full_name) return <Navigate to="/complete-profile" />;
  return children;
};

// Admins only; the server also refuses non-admins
const AdminRoute = ({ children }) => {
  const { isAdmin } = useAuth();
  return <ProtectedRoute>{isAdmin ? children : <Navigate to="/dashboard" />}</ProtectedRoute>;
};

// Requires authentication but full_name NOT yet set
const ProfileRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (user?.full_name) return <Navigate to="/dashboard" />;
  return children;
};

// Public only (redirect to dashboard if authenticated + has full_name)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <LoadingScreen />;
  if (isAuthenticated && !user?.full_name) return <Navigate to="/complete-profile" />;
  if (isAuthenticated) return <Navigate to="/dashboard" />;
  return children;
};

function AppRoutes() {
  return (
    <Router>
      <MaintenanceGate>
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
<Route path="/complete-profile" element={<ProfileRoute><ProfileCompletionPage /></ProfileRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsersPage /></AdminRoute>} />
        <Route path="/editor/design/:id" element={<ProtectedRoute><MasEditorPage mode="design" /></ProtectedRoute>} />
        <Route path="/editor/template/:id" element={<ProtectedRoute><MasEditorPage mode="template" /></ProtectedRoute>} />
        <Route path="/" element={<Navigate to="/dashboard" />} />
      </Routes>
      </MaintenanceGate>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
